import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Filter, ChevronRight, Star, ShieldCheck, Zap } from 'lucide-react';

const SellerStorePage: React.FC = () => {
  const { sellerId } = useParams();
  const [storeData, setStoreData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const fetchStore = async () => {
      try {
        // Port 2026 as per your current setup
        const response = await axios.get(`/nodejs/api/products/store/${sellerId}`);
        setStoreData(response.data);
      } catch (error) {
        console.error("Error fetching store:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
  }, [sellerId]);

  if (loading) return <div className="flex justify-center items-center h-screen text-blue-600 animate-pulse font-bold">Loading Moglix-style Store...</div>;
  if (!storeData) return <div className="text-center mt-20">Store Not Found</div>;

  const categories = ['All', ...Array.from(new Set(storeData.products.map((p: any) => p.category)))];
  const filteredProducts = selectedCategory === 'All' 
    ? storeData.products 
    : storeData.products.filter((p: any) => p.category === selectedCategory);

  return (
    <div className="bg-[#f2f2f2] min-h-screen pb-10 font-sans">
      {/* 1. COMPACT BRAND HEADER */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex flex-wrap items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gray-100 border flex items-center justify-center rounded shadow-inner text-xl font-black text-red-600">
              {storeData.seller.company_name.charAt(0)}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                {storeData.seller.company_name}
                <ShieldCheck size={18} className="text-green-600" />
              </h1>
              <p className="text-xs text-gray-500 font-medium">
                {storeData.seller.city}, {storeData.seller.state} | <span className="text-blue-600">Verified Plus Supplier</span>
              </p>
            </div>
          </div>
          <div className="flex gap-4 mt-4 lg:mt-0">
             <button className="bg-red-600 text-white px-5 py-2 text-sm font-bold rounded hover:bg-red-700 transition">Get Best Quote</button>
             <button className="border border-gray-300 px-5 py-2 text-sm font-bold rounded hover:bg-gray-50">Follow Store</button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row gap-4 mt-4 px-2 sm:px-4">
        
{/*         
        <aside className="w-full lg:w-64 flex-shrink-0">
          <div className="bg-white rounded border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b flex items-center gap-2 font-bold text-sm text-gray-700">
              <Filter size={14} /> CATEGORIES
            </div>
            <div className="py-2">
              {categories.map((cat: any) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between group transition-colors ${
                    selectedCategory === cat ? 'bg-red-50 text-red-600 font-bold border-r-4 border-red-600' : 'text-gray-600 hover:text-red-600'
                  }`}
                >
                  {cat}
                  <ChevronRight size={14} className={selectedCategory === cat ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} />
                </button>
              ))}
            </div>
            
            <div className="bg-gray-50 px-4 py-3 border-y font-bold text-sm text-gray-700 mt-4">PRICE RANGE</div>
            <div className="p-4 space-y-3">
               {['Below ₹500', '₹500 - ₹2000', '₹2000+'].map((range) => (
                 <label key={range} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                   <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-red-600" /> {range}
                 </label>
               ))}
            </div>
          </div>
        </aside> */}

        {/* 3. COMPACT PRODUCT GRID */}
        <main className="flex-1">
          {/* <div className="bg-white p-3 border border-gray-200 rounded shadow-sm mb-4 flex justify-between items-center">
            <span className="text-sm font-bold text-gray-700 uppercase tracking-tight">
              Showing {filteredProducts.length} Results in {selectedCategory}
            </span>
            <select className="text-xs border-gray-300 rounded p-1 focus:ring-red-500">
              <option>Relevance</option>
              <option>Price: Low to High</option>
            </select>
          </div> */}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
            {filteredProducts.map((product: any) => (
              <div key={product.id} className="bg-white border border-gray-200 rounded hover:shadow-md transition-all group relative">
                {/* Product Image - Smaller Size */}
                <div className="h-40 p-3 flex items-center justify-center bg-white relative overflow-hidden">
                  <img src={product.imageUrl} alt={product.name} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform" />
                  <div className="absolute top-2 left-2 bg-green-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">8% OFF</div>
                </div>

                {/* Product Info */}
                <div className="p-3 border-t border-gray-100 min-h-[140px] flex flex-col justify-between">
                  <div>
                    <h3 className="text-xs text-gray-700 font-medium line-clamp-2 h-8 leading-relaxed mb-1 group-hover:text-red-600">
                      {product.name}
                    </h3>
                    <div className="flex items-center gap-1 mb-2">
                       <div className="flex text-yellow-400"><Star size={10} fill="currentColor"/><Star size={10} fill="currentColor"/><Star size={10} fill="currentColor"/><Star size={10} fill="currentColor"/></div>
                       <span className="text-[10px] text-gray-400">(24)</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-black text-gray-900">₹{product.price}</span>
                      <span className="text-[10px] text-gray-400 line-through font-medium tracking-tighter">₹{Math.round(product.price * 1.08)}</span>
                    </div>
                    <div className="text-[10px] text-green-600 font-bold mb-3 flex items-center gap-1">
                      <Zap size={10} fill="currentColor"/> Ships in 24 Hours
                    </div>
                    <Link 
                      to={`/product/${product.id}`} 
                      className="block w-full text-center bg-white border border-red-600 text-red-600 py-1.5 rounded text-[11px] font-bold hover:bg-red-600 hover:text-white transition-all uppercase"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SellerStorePage;